import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Bell, Send, Save, HelpCircle } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationSettings() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [botToken, setBotToken] = useState("");
  const [chatId, setChatId] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [configured, setConfigured] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/settings/telegram`).then(({ data }) => {
      setEnabled(!!data.enabled);
      setChatId(data.chat_id || "");
      setConfigured(!!data.has_token);
    }).catch(() => {});
  }, [open]);

  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/settings/telegram`, {
        bot_token: botToken || undefined,
        chat_id: chatId,
        enabled,
      });
      toast.success(t("notif_saved"));
      setConfigured(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("notif_test_fail"));
    } finally {
      setBusy(false);
    }
  };

  const testMsg = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/settings/telegram/test`, {
        bot_token: botToken,
        chat_id: chatId,
        enabled,
      });
      toast.success(t("notif_test_sent"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("notif_test_fail"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-zinc-700 hover:bg-zinc-900 hover:text-white rounded-sm gap-2"
          data-testid="notification-settings-button"
        >
          <Bell className={enabled ? "h-3.5 w-3.5 text-emerald-400" : "h-3.5 w-3.5"} />
          <span className="hidden sm:inline">{t("notif_title")}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white sm:max-w-md" dir="auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-emerald-400" />
            {t("notif_title")}
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">
            {t("notif_desc")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="bot-token" className="text-xs text-zinc-400">
              {t("notif_bot_token")} {configured && !botToken && <span className="text-emerald-500">✓</span>}
            </Label>
            <Input
              id="bot-token"
              type="password"
              placeholder="123456:ABC-DEF..."
              value={botToken}
              onChange={(e) => setBotToken(e.target.value)}
              className="bg-zinc-900 border-zinc-800 rounded-sm num text-white"
              data-testid="input-bot-token"
              dir="ltr"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="chat-id" className="text-xs text-zinc-400">{t("notif_chat_id")}</Label>
            <Input
              id="chat-id"
              placeholder="123456789"
              value={chatId}
              onChange={(e) => setChatId(e.target.value)}
              className="bg-zinc-900 border-zinc-800 rounded-sm num text-white"
              data-testid="input-chat-id"
              dir="ltr"
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-zinc-900/60 border border-zinc-800 rounded-sm">
            <Label htmlFor="enable" className="text-sm text-white cursor-pointer">{t("notif_enable")}</Label>
            <Switch
              id="enable"
              checked={enabled}
              onCheckedChange={setEnabled}
              data-testid="switch-enable-notif"
            />
          </div>

          <div className="flex items-start gap-2 text-[11px] text-zinc-500 bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3">
            <HelpCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            <p className="leading-relaxed">{t("notif_help")}</p>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Button
              onClick={save}
              disabled={busy}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-black font-bold rounded-sm gap-2"
              data-testid="save-notif"
            >
              <Save className="h-3.5 w-3.5" />
              {t("notif_save")}
            </Button>
            <Button
              onClick={testMsg}
              disabled={busy || !chatId || (!botToken && !configured)}
              variant="outline"
              className="border-zinc-700 hover:bg-zinc-900 hover:text-white rounded-sm gap-2"
              data-testid="test-notif"
            >
              <Send className="h-3.5 w-3.5" />
              {t("notif_test")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
